import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowClockwise, Rows, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { fmt } from "../lib/format";
import KpiCard from "../components/KpiCard";
import ChartWidget from "../components/ChartWidget";
import PenjaminPanel from "../components/PenjaminPanel";
import TopListPanel from "../components/TopListPanel";
import KunjunganSection from "../components/KunjunganSection";
import DashboardLoading from "../components/DashboardLoading";
import FilterBar from "../components/FilterBar";
import DataTablePanel from "../components/DataTablePanel";
import AiInsights from "../components/AiInsights";
import { Button } from "../components/ui/button";

function formatRefreshed(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta",
    }).replace(/\./g, ":") + " WIB";
  } catch { return iso; }
}

export default function Dashboard() {
  const { id } = useParams();
  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [penjaminOn, setPenjaminOn] = useState(null);
  const [month, setMonth] = useState(null);
  const [kunjunganMonths, setKunjunganMonths] = useState([]);

  useEffect(() => { setFilters([]); setPenjaminOn(null); setMonth(null); }, [id]);

  const loadMeta = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get(`/datasources/${id}`);
      setMeta(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  }, [id]);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.post(`/datasources/${id}/summary`, { filters });
      setSummary(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  }, [id, filters]);

  useEffect(() => { setMeta(null); setSummary(null); loadMeta(); }, [loadMeta]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post(`/datasources/${id}/refresh`);
      setMeta(data);
      await loadSummary();
      toast.success(`Data diperbarui: ${fmt(data.row_count)} baris`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setRefreshing(false); }
  };

  if (error && !meta) {
    return (
      <div className="p-6">
        <div className="rounded-2xl border border-destructive/40 bg-destructive/10 text-destructive p-6 text-sm" data-testid="dashboard-error">
          {error}
        </div>
      </div>
    );
  }

  if (!meta || !summary) {
    return <DashboardLoading />;
  }

  const numeric = summary.numeric || [];
  const columns = meta.columns || [];
  const dateCol = columns.find((c) => c.type === "date");
  const catCol = columns.find((c) => c.type === "string");
  const dokterCol = columns.find((c) => /dokter/i.test(c.name) && c.type === "string");
  const tagihanCol = columns.find((c) => /total\s*tagihan/i.test(c.name) && c.type === "number")
    || columns.find((c) => c.type === "number");
  const asuransiCol = columns.find((c) => /^asuransi$/i.test(c.name) && c.type === "string");
  const penjaminCol = columns.find((c) => /final\s*penjamin/i.test(c.name) && c.type === "string");
  const finalAmountCol = columns.find((c) => /final\s*amount/i.test(c.name) && c.type === "number");
  const doctorMeasureCol = finalAmountCol || tagihanCol;

  const kpiDefaults = numeric.slice(0, 4);

  return (
    <div className="p-4 lg:p-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="flex items-end justify-between gap-4 mb-5 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">Dashboard Analitik</p>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight mt-1" data-testid="dashboard-title">{meta.name}</h1>
          <p className="text-muted-foreground text-sm mt-1.5 flex items-center gap-1.5">
            <Rows size={14} /> <span className="tabular">{fmt(summary.row_count)}</span> baris {filters.length > 0 && "(terfilter)"} · {columns.length} kolom
          </p>
          {meta.last_refreshed && (
            <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1.5" data-testid="dashboard-last-refreshed">
              <Clock size={12} weight="fill" className="text-primary" /> Data diperbarui: <span className="font-semibold text-foreground">{formatRefreshed(meta.last_refreshed)}</span>
            </p>
          )}
        </div>
        <Button variant="outline" onClick={refresh} disabled={refreshing}
          className="rounded-xl gap-2 bg-card" data-testid="dashboard-refresh">
          <ArrowClockwise size={16} className={refreshing ? "animate-spin" : ""} /> Perbarui Data
        </Button>
      </motion.div>

      <div className="mb-5">
        <FilterBar dsId={id} columns={columns} filters={filters} setFilters={setFilters} />
      </div>

      {/* Penjamin summary (dbtarget) + top-5 doctors per penjamin */}
      <PenjaminPanel dsId={id} onAvailable={setPenjaminOn} filters={filters}
        doctorDim={dokterCol?.name} measure={doctorMeasureCol?.name} penjaminField={penjaminCol?.name}
        monthColumn={dateCol?.name} month={month} setMonth={setMonth} dataMonths={kunjunganMonths} />

      {/* Generic KPI row (only when Penjamin panel is confirmed unavailable) */}
      {penjaminOn === false && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="rounded-2xl bg-card p-4 h-full flex flex-col justify-between shadow-sm ring-1 ring-black/[0.03]" data-testid="kpi-rows">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">Total Baris</span>
            <Rows size={16} className="text-primary" />
          </div>
          <div className="font-display font-extrabold text-xl sm:text-2xl tracking-tight tabular mt-3">{fmt(summary.row_count)}</div>
          <div className="text-xs text-muted-foreground mt-2">jumlah record</div>
        </div>
        {kpiDefaults.map((m, i) => (
          <KpiCard key={`${m.column}-${i}`} numeric={numeric} defaultColumn={m.column}
            defaultAgg={i === 0 ? "sum" : i === 1 ? "avg" : "sum"} index={i} />
        ))}
        {kpiDefaults.length === 0 && (
          <div className="col-span-3 rounded-2xl border border-dashed border-border p-4 flex items-center text-xs text-muted-foreground">
            Tidak ada kolom numerik terdeteksi
          </div>
        )}
      </div>
      )}

      {/* Charts + AI */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 mb-4">
        <div className="lg:col-span-8 grid grid-cols-1 xl:grid-cols-2 gap-3">
          <ChartWidget dsId={id} columns={columns} filters={filters} title="Tren / Perbandingan" testid="chart-1"
            initial={{ type: dateCol ? "line" : "bar", dimension: dateCol?.name || catCol?.name,
              aggregation: numeric[0] ? "sum" : "count", measure: numeric[0]?.column }} />
          <ChartWidget dsId={id} columns={columns} filters={filters}
            title={dokterCol ? "Dokter Penyumbang Terbanyak" : "Distribusi"} testid="chart-2"
            month={dokterCol ? month : null} monthColumn={dokterCol ? dateCol?.name : null}
            initial={dokterCol && doctorMeasureCol
              ? { type: "pie", dimension: dokterCol.name, aggregation: "sum", measure: doctorMeasureCol.name }
              : { type: "pie", dimension: catCol?.name, aggregation: "count" }} />
        </div>
        <div className="lg:col-span-4 min-h-[380px]">
          {asuransiCol && tagihanCol && penjaminCol ? (
            <TopListPanel dsId={id} title="Penjualan Asuransi Terbanyak"
              dimension={asuransiCol.name} measure={tagihanCol.name}
              extraFilters={[{ column: penjaminCol.name, values: ["ASURANSI"] }]}
              filters={filters} limit={10} color="#5FA777" testid="top-asuransi-panel"
              month={month} monthColumn={dateCol?.name} />
          ) : (
            <AiInsights dsId={id} />
          )}
        </div>
      </div>

      {/* Populasi pasien per kecamatan (map + cluster) + diagnosa terbanyak */}
      <KunjunganSection dsId={id} month={month} onMonths={setKunjunganMonths} />

      {/* Analisa Kolom + Data Mentah -> paling bawah */}
      <div className="mt-6 mb-4">
        <ChartWidget dsId={id} columns={columns} filters={filters} title="Analisa Kolom" testid="chart-3"
          month={month} monthColumn={dateCol?.name}
          initial={{ type: "bar", dimension: catCol?.name, aggregation: numeric[0] ? "sum" : "count", measure: numeric[0]?.column }} />
      </div>
      <DataTablePanel dsId={id} columns={columns} filters={filters} />
    </div>
  );
}
